/**
 * @fileoverview Own the matter-scoped document workspace while reusing shared archive controls. Backend matter/document identity and capabilities govern queries and writes; shared metadata subscriptions own snapshots. Explorer, preview, transfer, and trash components retain focused responsibilities. `kc-internal-frontend/src/components/Documents/DocumentsPage.js` owns the firm-wide workspace. Matter filtering and browser hierarchy do not create separate canonical versions or bypass archive permissions.
 */

import useDocumentData from '../../store/Documents/useDocumentData';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import AppIcon from '../shared/AppIcon';
import { AppButton } from '../shared';
import { formatCreated } from '../Documents/archiveReviewUtils';
import DocumentFileTypeIcon from '../Documents/DocumentFileTypeIcon';
import { WORD_DOCUMENT_ACCEPT, isWordDocumentFilename } from '../Documents/documentFilePolicy';
import DocumentExplorerPreviewColumn from '../Documents/DocumentExplorerPreviewColumn';
import DocumentFileViewerModal from '../Documents/DocumentFileViewerModal';
import FolderDestinationModal, {
  folderDestinationKey,
  folderMovePayload,
} from '../Documents/FolderDestinationModal';
import FileContextMenu from '../Documents/FileContextMenu';
import DocumentTrashDialog from '../Documents/DocumentTrashDialog';
import FolderContextMenu from '../Documents/FolderContextMenu';
import NewFolderModal from '../Documents/NewFolderModal';
import useDocumentFileExplorerController, {
  documentFolderCount as folderCount,
  documentClipboardSelection,
  documentFolderName as folderName,
  normalizeDocumentFolderPath as normalizePath,
  shouldUseDocumentFileViewer,
} from '../Documents/DocumentFileExplorerController';
import DocumentTransferManager, {
  droppedFiles,
  isExternalFileDrag,
} from '../Documents/DocumentTransferManager';
import MillerColumnExplorer, {
  MillerColumn,
  MillerColumnEmpty,
  MillerColumnHeader,
  MillerColumnItem,
  MillerColumnList,
  MillerColumnRow,
} from '../Documents/MillerColumnExplorer';
import './MatterDocumentsTab.css';

const FILE_PAGE_SIZE = 25;
const LOAD_LIMIT = 500;




function sameMatter(folder, matterNumber) {
  return String(folder?.matter_number || '').trim().toLowerCase() ===
    String(matterNumber || '').trim().toLowerCase();
}



function folderIdentity(folder) {
  if (folder?.folder_type === 'simul' && folder.folder_id) return `simul:${folder.folder_id}`;
  if (folder?.folder_type === 'matter') {
    return `matter:${String(folder.matter_number || '').trim().toLowerCase()}`;
  }
  return `internal:${normalizePath(folder?.folder_path).toLowerCase()}`;
}



function uniqueFoldersByName(candidates) {
  const unique = new Map();
  candidates.forEach((folder) => {
    const key = `name:${folderName(folder).toLowerCase()}`;
    if (!unique.has(key)) unique.set(key, folder);
  });
  return [...unique.values()].sort((left, right) => folderName(left).localeCompare(folderName(right)));
}



function NewMatterDocumentModal({
  visible,
  matterNumber,
  folderPath,
  folderOptions,
  templates,
  submitting,
  error,
  onClose,
  onCreated,
}) {
  const [form, setForm] = useState({
    mode: 'blank',
    title: '',
    template_id: '',
    folder_path: normalizePath(folderPath),
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);

  const selectFiles =

  (files) => {
    if (submitting) return;
    const selected = Array.from(files || []);
    if (selected.length !== 1 || !isWordDocumentFilename(selected[0]?.name || '')) {
      setFile(null);
      setFileError('Choose one Word document.');
      return;
    }
    setFileError('');
    setFile(selected[0]);
    const previousFileTitle = file?.name.replace(/\.(docx?|docm|dotx?|dotm)$/i, '').trim();
    setForm((current) => ({
      ...current,
      mode: 'upload',
      title: !current.title.trim() || current.title === previousFileTitle
        ? selected[0].name.replace(/\.(docx?|docm|dotx?|dotm)$/i, '').trim()
        : current.title,
    }));
  };

  if (!visible) return null;
  const modes = [
    {
      value: 'blank',
      label: 'Blank',
      detail: 'Start writing',
      icon: 'pi pi-file-edit',
    },
    {
      value: 'template',
      label: 'Template',
      detail: 'Use approved language',
      icon: 'pi pi-copy',
    },
    {
      value: 'upload',
      label: 'Upload',
      detail: 'Import a Word document',
      icon: 'pi pi-upload',
    },
  ];
  const folderParts = normalizePath(form.folder_path).split('/').filter(Boolean);
  const disabled = submitting ||
    !form.title.trim() ||
    (form.mode === 'template' && !form.template_id) ||
    (form.mode === 'upload' && (!file || !isWordDocumentFilename(file.name)));

  return (
    <div
      className="matter-documents-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <form
        className="matter-documents-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="matter-new-document-title"
        onDragEnter={

        (event) => {
          if (!isExternalFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          event.stopPropagation();
          dragDepth.current += 1;
          if (!submitting) setDragActive(true);
        }}
        onDragOver={

        (event) => {
          if (!isExternalFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = submitting ? 'none' : 'copy';
        }}
        onDragLeave={(event) => {
          event.stopPropagation();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (!dragDepth.current) setDragActive(false);
        }}
        onDrop={

        (event) => {
          event.preventDefault();
          event.stopPropagation();
          dragDepth.current = 0;
          setDragActive(false);
          if (isExternalFileDrag(event.dataTransfer)) selectFiles(event.dataTransfer.files);
        }}
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled) onCreated(form, file, form.folder_path);
        }}>
        <header className="matter-documents-modal__header">
          <span className="matter-documents-modal__hero-icon">
            <AppIcon icon="pi pi-file-edit" aria-hidden="true" />
          </span>
          <div className="matter-documents-modal__heading">
            <h2 id="matter-new-document-title">New document</h2>
            <p>Choose how you want to get started.</p>
          </div>
          <button
            type="button"
            className="matter-documents-modal__close"
            aria-label="Close new document"
            onClick={onClose}>
            <AppIcon icon="pi pi-times" aria-hidden="true" />
          </button>
        </header>

        <div className="matter-documents-modal__destination">
          <span className="matter-documents-modal__destination-icon">
            <AppIcon icon="pi pi-folder" aria-hidden="true" />
          </span>
          <div>
            <span>Save in</span>
            <strong>
              <span>{matterNumber}</span>
              {folderParts.map((part, index) => (
                <React.Fragment key={folderParts.slice(0, index + 1).join('/')}>
                  <AppIcon icon="pi pi-angle-right" aria-hidden="true" />
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </strong>
            <label>
              <select
                aria-label="Save in folder"
                value={form.folder_path}
                onChange={(event) => {
                  const folder_path = event.target.value;
                  setForm((current) => ({ ...current, folder_path }));
                }}>
                <option value="">Matter folder</option>
                {folderOptions.filter(Boolean).map((path) => (
                  <option key={path} value={path}>{path}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="matter-documents-modal__body">
          <fieldset className="matter-documents-modal__field">
            <legend>Start from</legend>
            <div className="matter-documents-modal__modes" role="radiogroup" aria-label="Start from">
              {modes.map((mode) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.mode === mode.value}
                  className={form.mode === mode.value ? 'is-selected' : ''}
                  key={mode.value}
                  onClick={() => setForm((current) => ({ ...current, mode: mode.value }))}>
                  <span className="matter-documents-modal__mode-icon">
                    <AppIcon icon={mode.icon} aria-hidden="true" />
                  </span>
                  <span>
                    <strong>{mode.label}</strong>
                    <small>{mode.detail}</small>
                  </span>
                  <span className="matter-documents-modal__mode-check" aria-hidden="true">
                    <AppIcon icon="pi pi-check" />
                  </span>
                </button>
              ))}
            </div>
          </fieldset>

          <label className="matter-documents-modal__field">
            <span>Title</span>
            <span className="matter-documents-modal__input">
              <AppIcon icon="pi pi-file" aria-hidden="true" />
              <input
                value={form.title}
                aria-label="Title"
                placeholder="e.g. Settlement agreement"
                onChange={(event) => {
                  const title = event.target.value;
                  setForm((current) => ({ ...current, title }));
                }}
                autoFocus />
            </span>
          </label>

          {form.mode === 'template' && (
            <label className="matter-documents-modal__field">
              <span>Template</span>
              <select
                value={form.template_id}
                onChange={(event) => {
                  const template_id = event.target.value;
                  setForm((current) => ({ ...current, template_id }));
                }}>
                <option value="">Select a template</option>
                {templates.map((template) => (
                  <option key={template.template_id} value={template.template_id}>{template.title}</option>
                ))}
              </select>
            </label>
          )}

          {(form.mode === 'upload' || dragActive) && (
            <label className="matter-documents-modal__field">
              <span>Word file</span>
              <span className={`matter-documents-modal__file-picker${dragActive ? ' is-drag-active' : ''}`}>
                <input
                  type="file"
                  accept={WORD_DOCUMENT_ACCEPT}
                  aria-label="Word file"
                  disabled={submitting}
                  onChange={(event) => {
                    if (event.target.files?.length) selectFiles(event.target.files);
                    event.target.value = '';
                  }} />
                <span className="matter-documents-modal__file-icon">
                  {file
                    ? <DocumentFileTypeIcon file={file} aria-hidden="true" />
                    : <AppIcon icon="pi pi-upload" aria-hidden="true" />}
                </span>
                <span>
                  <strong>{dragActive ? 'Drop Word document here' : file?.name || 'Drop a Word file here'}</strong>
                  <small>{file ? 'Click or drop to replace' : 'or click to browse'}</small>
                </span>
              </span>
            </label>
          )}

          {fileError && <div className="matter-documents-error" role="alert">{fileError}</div>}
          {error && <div className="matter-documents-error" role="alert">{error}</div>}
        </div>

        <footer className="matter-documents-modal__footer">
          <button type="button" className="matter-documents-quiet-button" onClick={onClose}>Cancel</button>
          <AppButton
            label={submitting ? 'Creating' : 'Create document'}
            icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-plus'}
            disabled={disabled}
            type="submit" />
        </footer>
      </form>
    </div>
  );
}



function MatterDocumentsTab({ matterNumber }) {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const navigate = useNavigate();
  const dataCache = useDocumentData();
  const folderCatalogUrl = `/documents/folders?${new URLSearchParams({ matter_number: matterNumber })}`;
  const [retryAttempt, setRetryAttempt] = useState(0);
  const restoredView = dataCache.view(`matter:${matterNumber}`);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(restoredView?.currentFolder || null);
  const [baseSimulRoot, setBaseSimulRoot] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filePage, setFilePage] = useState(restoredView?.filePage || 0);
  const [newDocumentOpen, setNewDocumentOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [moveTarget, setMoveTarget] = useState(null);
  const [destinationFolders, setDestinationFolders] = useState(null);
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [newFolderTarget, setNewFolderTarget] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderSubmitting, setNewFolderSubmitting] = useState(false);
  const [newFolderError, setNewFolderError] = useState('');
  const [trashRequest, setTrashRequest] = useState(null);
  const [trashSubmitting, setTrashSubmitting] = useState(false);
  const [trashError, setTrashError] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [internalDropTargetKey, setInternalDropTargetKey] = useState('');
  const [navigatorSearch, setNavigatorSearch] = useState(restoredView?.navigatorSearch || '');
  const [uploadDropTargetKey, setUploadDropTargetKey] = useState('');
  const [pendingUpload, setPendingUpload] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const columnsViewportRef = useRef(null);
  const uploadStatusTimerRef = useRef(null);

  const updateUploadStatus = useCallback(

  (message, clearAfter = 0) => {
    window.clearTimeout(uploadStatusTimerRef.current);
    setUploadStatus(message);
    if (message && clearAfter) {
      uploadStatusTimerRef.current = window.setTimeout(() => setUploadStatus(''), clearAfter);
    }
  }, []);

  useEffect(() => () => window.clearTimeout(uploadStatusTimerRef.current), []);

  const simulFolders = useMemo(
    () => folders.filter((folder) => folder.folder_type === 'simul' && sameMatter(folder, matterNumber)),
    [folders, matterNumber]
  );
  const simulById = useMemo(
    () => new Map(simulFolders.map((folder) => [String(folder.folder_id), folder])),
    [simulFolders]
  );
  const simulRoots = useMemo(
    () => simulFolders.filter((folder) => !simulById.has(String(folder.parent_folder_id || ''))),
    [simulById, simulFolders]
  );
  const internalFolders = useMemo(
    () => folders.filter((folder) => folder.folder_type === 'location' && sameMatter(folder, matterNumber)),
    [folders, matterNumber]
  );
  const matterCreateParent = useMemo(() => (
    folders.find((folder) => folder.folder_type === 'matter' && sameMatter(folder, matterNumber)) || {
      folder_type: 'matter',
      matter_number: matterNumber,
      name: matterNumber,
    }
  ), [folders, matterNumber]);

  const relativePathFor = useCallback(

  (folder) => {
    if (!folder || folder.folder_type === 'matter') return '';
    if (folder.folder_type !== 'simul') return normalizePath(folder.folder_path);
    const parts = [];
    let cursor = folder;
    const visited = new Set();
    while (cursor?.folder_id && cursor.folder_id !== baseSimulRoot?.folder_id && !visited.has(cursor.folder_id)) {
      visited.add(cursor.folder_id);
      parts.unshift(folderName(cursor));
      cursor = simulById.get(String(cursor.parent_folder_id || ''));
    }
    return normalizePath(parts.join('/'));
  }, [baseSimulRoot?.folder_id, simulById]);

  const currentPath = relativePathFor(currentFolder);
  const creationFolderOptions = useMemo(() => (
    [...new Set([
      '',
      ...simulFolders.map(relativePathFor),
      ...internalFolders.map((folder) => normalizePath(folder.folder_path)),
    ])].sort((left, right) => left.localeCompare(right))
  ), [internalFolders, relativePathFor, simulFolders]);

  const immediateInternalChildren = useCallback(

  (parentPath) => {
    const normalizedParent = normalizePath(parentPath);
    return internalFolders.filter(

    (folder) => {
      const path = normalizePath(folder.folder_path);
      if (!path || path === normalizedParent) return false;
      const remainder = normalizedParent
        ? path.startsWith(`${normalizedParent}/`) ? path.slice(normalizedParent.length + 1) : ''
        : path;
      return remainder && !remainder.includes('/');
    });
  }, [internalFolders]);

  const childFolders = useMemo(() => {
    let candidates;
    if (!currentFolder) {
      candidates = [
        ...simulRoots,
        ...immediateInternalChildren(''),
      ];
    } else if (currentFolder.folder_type === 'simul') {
      candidates = [
        ...simulFolders.filter((folder) => String(folder.parent_folder_id || '') === String(currentFolder.folder_id)),
        ...immediateInternalChildren(currentPath),
      ];
    } else {
      candidates = immediateInternalChildren(currentPath);
    }
    return uniqueFoldersByName(candidates);
  }, [currentFolder, currentPath, immediateInternalChildren, simulFolders, simulRoots]);

  const breadcrumbs = useMemo(

  () => {
    if (!currentFolder || !baseSimulRoot || currentFolder.folder_type !== 'simul') return [];
    const trail = [];
    let cursor = currentFolder;
    const visited = new Set();
    while (cursor?.folder_id && cursor.folder_id !== baseSimulRoot.folder_id && !visited.has(cursor.folder_id)) {
      visited.add(cursor.folder_id);
      trail.unshift(cursor);
      cursor = simulById.get(String(cursor.parent_folder_id || ''));
    }
    return trail;
  }, [baseSimulRoot, currentFolder, simulById]);

  const loadDocuments = useCallback(

  (folder, relativePath, { force = true } = {}) => {
    let url;
    if (folder?.folder_id && folder.folder_type !== 'simul') {
      url = `/documents/folders/${encodeURIComponent(folder.folder_id)}/documents?first=0&rows=${LOAD_LIMIT}`;
    } else if (folder?.folder_type === 'simul') {
      url = `/simul/archive/folders/${encodeURIComponent(folder.folder_id)}/documents?first=0&rows=${LOAD_LIMIT}`;
    } else {
      const params = new URLSearchParams({ first: '0', rows: String(LOAD_LIMIT), registry_status: 'all',
        matter_number: matterNumber, folder_scope: 'direct' });
      if (relativePath) params.set('folder_path', relativePath);
      url = `/document-library/search?${params}`;
    }
    setLoading(!dataCache.read(url));
    setError('');
    if (!dataCache.read(url)) setDocuments([]);
    return dataCache.load('matter-documents', url, (data) => {
      setDocuments([...(data.documents || [])].sort((left, right) => String(left.filename || '').localeCompare(String(right.filename || ''))));
      setLoading(false);
    }, { force, onClear: () => setDocuments([]), onError: (requestError) => {
      setLoading(false);
      setError(requestError.response?.data?.error || requestError.message || 'Failed to load matter documents.');
    } }).catch(() => null);
  }, [dataCache, matterNumber]);

  useEffect(

  () => {
    let active = true;
    const restored = dataCache.view(`matter:${matterNumber}`);
    let initialized = false;
    setLoading(!dataCache.read(folderCatalogUrl));
    setError('');
    setFilePage(restored?.filePage || 0);
    setNavigatorSearch(restored?.navigatorSearch || '');
    setCurrentFolder(restored?.currentFolder || null);
    dataCache.load('matter-catalog', folderCatalogUrl,

    (data) => {
        const response = { data };
        if (!active) return;
        const catalog = response.data?.folders || [];
        const matchingSimul = catalog.filter((folder) => folder.folder_type === 'simul' && sameMatter(folder, matterNumber));
        const matchingIds = new Set(matchingSimul.map((folder) => String(folder.folder_id)));
        const roots = matchingSimul.filter((folder) => !matchingIds.has(String(folder.parent_folder_id || '')));
        const initialRoot = roots.length === 1 ? roots[0] : null;
        const initialExplorerRoot = initialRoot || catalog.find((folder) => (
          folder.folder_type === 'matter' && sameMatter(folder, matterNumber)
        )) || {
          folder_type: 'matter',
          matter_number: matterNumber,
          name: matterNumber,
        };
        const matchingInternal = catalog.filter((folder) => (
          folder.folder_type === 'location' && sameMatter(folder, matterNumber)
        ));
        const initialChildren = uniqueFoldersByName([
          ...(initialRoot
            ? matchingSimul.filter((folder) => String(folder.parent_folder_id || '') === String(initialRoot.folder_id))
            : roots),
          ...matchingInternal.filter((folder) => {
            const path = normalizePath(folder.folder_path);
            return path && !path.includes('/');
          }),
        ]);
        const restoredFolder = restored?.currentFolder && catalog.find((candidate) => folderIdentity(candidate) === folderIdentity(restored.currentFolder));
        const initialFolder = restoredFolder || (initialChildren.length === 1 ? initialChildren[0] : initialExplorerRoot);
        const initialPath = initialFolder && initialFolder !== initialRoot
          ? initialFolder.folder_type === 'simul'
            ? folderName(initialFolder)
            : normalizePath(initialFolder.folder_path)
          : '';
        setFolders(catalog);
        setBaseSimulRoot(initialRoot);
        if (initialized) return;
        initialized = true;
        setCurrentFolder(initialFolder);
        return loadDocuments(initialFolder, initialPath, { force: retryAttempt > 0 });
      }, { force: retryAttempt > 0 })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError?.response?.data?.error || requestError.message || 'Failed to load matter folders.');
        setLoading(false);
      });
    return () => {
      active = false;
      dataCache.stop('matter-catalog');
      dataCache.stop('matter-documents');
    };
  }, [dataCache, folderCatalogUrl, loadDocuments, matterNumber, retryAttempt]);

  useEffect(() => {
    if (!currentFolder || !sameMatter(currentFolder, matterNumber)) return;
    dataCache.remember(`matter:${matterNumber}`, { currentFolder, filePage, navigatorSearch });
  }, [currentFolder, dataCache, filePage, matterNumber, navigatorSearch]);

  const openFolder =

  (folder) => {
    setNavigatorSearch('');
    setCurrentFolder(folder);
    setFilePage(0);
    loadDocuments(folder, relativePathFor(folder), { force: false });
  };

  const goToMatterRoot =

  () => {
    setNavigatorSearch('');
    const rootFolder = baseSimulRoot || matterCreateParent;
    setCurrentFolder(rootFolder);
    setFilePage(0);
    loadDocuments(rootFolder, '', { force: false });
  };

  const goUp =

  () => {
    if (!currentFolder) return;
    if (currentFolder.folder_type === 'simul') {
      const parent = simulById.get(String(currentFolder.parent_folder_id || ''));
      if (!parent || currentFolder.folder_id === baseSimulRoot?.folder_id) {
        goToMatterRoot();
        return;
      }
      openFolder(parent);
      return;
    }
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    const parentPath = parts.join('/');
    const parent = internalFolders.find((folder) => normalizePath(folder.folder_path) === parentPath);
    if (parent) openFolder(parent);
    else goToMatterRoot();
  };

  const openNewDocument =

  () => {
    setCreateError('');
    setNewDocumentOpen(true);
    api.get('/simul/archive/templates')
      .then((response) => setTemplates(response.data?.templates || []))
      .catch(() => setTemplates([]));
  };

  // Every matter folder can take a subfolder. KC folders are stored as paths under
  // the matter, and a Simul folder shows the KC folders whose path matches its own,
  // so a subfolder of a Simul folder is a KC folder at that path (the backend refuses
  // to parent anything to a Simul folder directly).
  const openNewFolder =

  (target = currentFolder) => {
    const atRoot = !target || target.folder_type === 'matter' || !relativePathFor(target);
    setNewFolderTarget(atRoot ? matterCreateParent : target);
    setNewFolderName('');
    setNewFolderError('');
  };

  const submitNewFolder =

  () => {
    const target = newFolderTarget;
    const name = String(newFolderName || '').trim();
    if (!target || !name) return;
    if (/[\\/]/.test(name)) {
      setNewFolderError('Use a single folder name without slashes.');
      return;
    }
    if (treeChildrenFor(target).some((folder) => folderName(folder).toLowerCase() === name.toLowerCase())) {
      setNewFolderError(`A folder named ${name} is already here.`);
      return;
    }
    const parentPath = relativePathFor(target);
    const payload = target.folder_type === 'location' && target.folder_id
      ? { matter_number: matterNumber, parent_folder_id: target.folder_id, folder_name: name }
      : {
          matter_number: matterNumber,
          folder_path: normalizePath([parentPath, name].filter(Boolean).join('/')),
          ...(matterCreateParent.folder_id ? { parent_folder_id: matterCreateParent.folder_id } : {}),
        };
    setNewFolderSubmitting(true);
    setNewFolderError('');
    api.post('/documents/folders', payload)
      .then((response) => {
        const folder = response.data?.folder;
        setNewFolderTarget(null);
        if (folder) {
          setFolders((current) => [...current.filter((candidate) => (
            folderDestinationKey(candidate) !== folderDestinationKey(folder)
          )), folder]);
        }
        return api.get(folderCatalogUrl).then((catalog) => setFolders(catalog.data?.folders || []));
      })
      .catch((requestError) => setNewFolderError(
        requestError?.response?.data?.error || requestError.message || 'Could not create folder.'
      ))
      .finally(() => setNewFolderSubmitting(false));
  };

  const createDocument =

  (form, file, destinationPath) => {
    const normalizedDestination = normalizePath(destinationPath);
    const destinationFolder = normalizedDestination
      ? internalFolders.find((folder) => normalizePath(folder.folder_path) === normalizedDestination)
      : folders.find((folder) => folder.folder_type === 'matter' && sameMatter(folder, matterNumber));
    const destinationFolderId = String(destinationFolder?.folder_id || '').trim();
    setCreating(true);
    setCreateError('');
    let request;
    if (form.mode === 'upload') {
      const data = new FormData();
      data.append('mode', 'upload');
      data.append('file', file);
      data.append('title', form.title);
      data.append('matter_number', matterNumber);
      data.append('folder_path', normalizedDestination);
      if (destinationFolderId) data.append('folder_id', destinationFolderId);
      request = api.post('/simul/archive/documents', data);
    } else {
      request = api.post('/simul/archive/documents', {
        ...form,
        matter_number: matterNumber,
        folder_path: normalizedDestination,
        ...(destinationFolderId ? { folder_id: destinationFolderId } : {}),
        filename: `${form.title}.docx`,
      });
    }
    request
      .then(

      (response) => {
        if (!mountedRef.current) return null;
        const documentId = response.data?.document?.document_id;
        setNewDocumentOpen(false);
        if (documentId) navigate(`/documents/${encodeURIComponent(documentId)}`);
        else return loadDocuments(currentFolder, normalizedDestination);
        return null;
      })
      .catch((requestError) => {
        if (!mountedRef.current) return;
        setCreateError(requestError?.response?.data?.error || requestError.message || 'Failed to create document.');
      })
      .finally(() => { if (mountedRef.current) setCreating(false); });
  };

  const openMoveFile =

  (document) => {
    closeFileContextMenu();
    setMoveError('');
    setMoveTarget({ type: 'file', item: document });
  };

  const requestDocumentTrash = useCallback(

  (document) => {
    const selection = documentClipboardSelection('file', document);
    if (!selection || trashSubmitting) return;
    setTrashError('');
    setTrashRequest({
      count: 1,
      selection,
      filename: document.filename || 'Untitled document',
      returnFocus: window.document.activeElement,
    });
  }, [trashSubmitting]);

  const closeTrashRequest = useCallback(

  () => {
    setTrashRequest((current) => {
      window.setTimeout(() => current?.returnFocus?.focus?.(), 0);
      return null;
    });
    setTrashError('');
  }, []);

  const confirmDocumentTrash = useCallback(

  async () => {
    if (!trashRequest || trashSubmitting) return;
    setTrashSubmitting(true);
    setTrashError('');
    try {
      const operationResponse = await api.post('/simul/archive/operations', {
        action: 'trash',
        selection: trashRequest.selection,
        options: {},
        idempotency_key: `trash:${Date.now()}:${Math.random().toString(36).slice(2)}`,
      });
      const failed = (operationResponse.data?.operation?.results || []).find((result) => result.status === 'failed');
      if (failed) throw new Error(failed.error || 'Could not move this document to trash.');
      const response = await api.get(folderCatalogUrl);
      setFolders(response.data?.folders || []);
      await loadDocuments(currentFolder, currentPath);
      updateUploadStatus(`${trashRequest.filename} moved to trash`, 4000);
      closeTrashRequest();
    } catch (requestError) {
      setTrashError(requestError?.response?.data?.error || requestError.message || 'Could not move this document to trash.');
    } finally {
      setTrashSubmitting(false);
    }
  }, [closeTrashRequest, currentFolder, currentPath, folderCatalogUrl, loadDocuments, trashRequest, trashSubmitting, updateUploadStatus]);

  const openMoveFolder =

  (folder) => {
    if (!folder?.folder_id || !folder.can_move_internally) return;
    setMoveError('');
    setMoveTarget({ type: 'folder', item: folder });
  };

  // Cross-matter destinations need the full catalog only when moving an item.
  useEffect(

  () => {
    if (!moveTarget) {
      setDestinationFolders(null);
      return undefined;
    }
    let active = true;
    dataCache.load('matter-destinations', '/documents/folders', (data) => {
      if (active) setDestinationFolders(data.folders || []);
    }).catch((requestError) => {
      if (active) setMoveError(requestError.message || 'Could not load destination folders.');
    });
    return () => {
      active = false;
      dataCache.stop('matter-destinations');
    };
  }, [dataCache, moveTarget]);

  const moveExcludedFolderIds = useMemo(

  () => {
    const item = moveTarget?.item;
    if (!item) return [];
    if (moveTarget.type === 'file') return item.folder_id ? [item.folder_id] : [];
    const rootId = String(item.folder_id || '');
    const excluded = new Set([rootId].filter(Boolean));
    const pending = [rootId].filter(Boolean);
    while (pending.length > 0) {
      const parentId = pending.shift();
      for (const folder of folders) {
        const folderId = String(folder.folder_id || '');
        if (folderId && String(folder.parent_folder_id || '') === parentId && !excluded.has(folderId)) {
          excluded.add(folderId);
          pending.push(folderId);
        }
      }
    }
    if (item.parent_folder_id) excluded.add(String(item.parent_folder_id));
    return [...excluded];
  }, [folders, moveTarget]);

  const moveExcludedDestinationKeys = useMemo(() => {
    const item = moveTarget?.item;
    if (!item || item.folder_id) return [];
    return [folderDestinationKey({
      matter_number: item.matter_number,
      folder_path: item.folder_path,
      folder_type: normalizePath(item.folder_path) ? 'location' : 'matter',
    })];
  }, [moveTarget]);

  const createMoveDestination =

  async (payload) => {
    const response = await api.post('/documents/folders', payload);
    const folder = response.data?.folder;
    if (!folder) throw new Error('The folder was created, but its destination could not be loaded.');
    setFolders((current) => [...current.filter((candidate) => (
      folderDestinationKey(candidate) !== folderDestinationKey(folder)
    )), folder]);
    return folder;
  };

  const submitMove =

  (destination) => {
    if (!moveTarget?.item) return;
    const isFolderMove = moveTarget.type === 'folder';
    setMoveSubmitting(true);
    setMoveError('');
    const request = isFolderMove
      ? api.patch(`/documents/folders/${encodeURIComponent(moveTarget.item.folder_id)}/location`, {
          destination_parent_id: String(destination?.folder_id || '').trim(),
        })
      : api.patch(
          `/simul/archive/documents/${encodeURIComponent(moveTarget.item.document_id)}/location`,
          folderMovePayload(destination)
        );
    request
      .then(() => api.get(folderCatalogUrl))
      .then((response) => {
        setFolders(response.data?.folders || []);
        setMoveTarget(null);
        return loadDocuments(currentFolder, currentPath);
      })
      .catch((requestError) => setMoveError(
        requestError?.response?.data?.error || requestError.message || `Failed to move ${moveTarget.type}.`
      ))
      .finally(() => setMoveSubmitting(false));
  };

  const performPaste = useCallback(

  async (clipboard, destination) => {
    setMoveError('');
    const operationResponse = await api.post('/simul/archive/operations', {
      action: clipboard.action,
      selection: clipboard.selection,
      options: {
        destination: folderMovePayload(destination),
        conflict_policy: 'keep_both',
      },
      idempotency_key: `${clipboard.action}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
    });
    const response = await api.get(folderCatalogUrl);
    setFolders(response.data?.folders || []);
    await loadDocuments(currentFolder, currentPath);
    return operationResponse.data?.operation;
  }, [currentFolder, currentPath, folderCatalogUrl, loadDocuments]);

  const beginInternalDrag =

  (event, type, item) => {
    const identifier = type === 'folder' ? item.folder_id : item.document_id;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${type}:${identifier || ''}`);
    setDraggedItem({ type, item });
    setMoveError('');
  };

  const endInternalDrag =

  () => {
    setDraggedItem(null);
    setInternalDropTargetKey('');
  };

  const canDropInternally =

  (destination) => {
    if (!draggedItem || !destination) return false;
    if (draggedItem.type === 'file') {
      if (destination.folder_id && draggedItem.item.folder_id) {
        return String(destination.folder_id) !== String(draggedItem.item.folder_id);
      }
      return normalizePath(relativePathFor(destination)) !== normalizePath(draggedItem.item.folder_path);
    }
    const source = draggedItem.item;
    if (!source?.can_move_internally || !source.folder_id || !destination.folder_id) return false;
    if ([source.folder_id, source.parent_folder_id].map(String).includes(String(destination.folder_id))) return false;
    const byId = new Map(folders.map((folder) => [String(folder.folder_id || ''), folder]));
    let ancestor = destination;
    while (ancestor) {
      if (String(ancestor.folder_id || '') === String(source.folder_id)) return false;
      ancestor = byId.get(String(ancestor.parent_folder_id || '')) || null;
    }
    return true;
  };

  const dropInternal =

  async (event, destination) => {
    if (!canDropInternally(destination)) return;
    event.preventDefault();
    event.stopPropagation();
    const source = draggedItem;
    setInternalDropTargetKey('');
    try {
      if (source.type === 'folder') {
        await api.patch(`/documents/folders/${encodeURIComponent(source.item.folder_id)}/location`, {
          destination_parent_id: String(destination.folder_id || ''),
        });
      } else {
        await api.patch(`/simul/archive/documents/${encodeURIComponent(source.item.document_id)}/location`, folderMovePayload(destination));
      }
      const response = await api.get(folderCatalogUrl);
      setFolders(response.data?.folders || []);
      await loadDocuments(currentFolder, currentPath);
      updateUploadStatus(`${source.item.name || source.item.filename || 'Item'} moved to ${folderName(destination)}`, 4000);
    } catch (requestError) {
      setMoveError(requestError?.response?.data?.error || requestError.message || `Could not move ${source.type}.`);
    } finally {
      setDraggedItem(null);
    }
  };

  const uploadTargetKey =

  (kind, folder) => `${kind}:${folderIdentity(folder)}`;
  const uploadFolderPath =

  (folder) => relativePathFor(folder);
  const uploadDestinationLabel =

  (folder) => {
    const path = uploadFolderPath(folder);
    return path ? `${matterNumber} / ${path}` : matterNumber;
  };
  const uploadDestinationFolderId =

  (folder) => (
    folder?.folder_type === 'simul' ? '' : String(folder?.folder_id || '')
  );
  const externalDropHandlers =

  (folder, kind) => {
    const targetKey = uploadTargetKey(kind, folder);
    const markTarget =

    (event) => {
      if (!folder) return;
      if (isExternalFileDrag(event.dataTransfer)) {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
        setUploadDropTargetKey(targetKey);
        return;
      }
      if (!canDropInternally(folder)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setInternalDropTargetKey(targetKey);
    };
    return {
      onDragEnter: markTarget,
      onDragOver: markTarget,
      onDragLeave:

      (event) => {
        if (isExternalFileDrag(event.dataTransfer)) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.currentTarget.contains(event.relatedTarget)) setUploadDropTargetKey('');
          return;
        }
        if (!event.currentTarget.contains(event.relatedTarget)) setInternalDropTargetKey('');
      },
      onDrop:

      async (event) => {
        if (!folder) return;
        if (!isExternalFileDrag(event.dataTransfer)) {
          await dropInternal(event, folder);
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const transfer = event.dataTransfer;
        setUploadDropTargetKey('');
        setUploadError('');
        try {
          const files = await droppedFiles(transfer);
          if (!files.length) {
            setUploadError('No files were found in that drop. Try dropping the files or folder again.');
            return;
          }
          setPendingUpload({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            files,
            folder,
          });
          updateUploadStatus(`Uploading ${files.length} file${files.length === 1 ? '' : 's'} to ${uploadDestinationLabel(folder)}…`);
        } catch (dropError) {
          setUploadError(dropError?.message || 'The dropped files could not be read.');
          updateUploadStatus('');
        }
      },
    };
  };

  const refreshAfterUpload =

  () => {
    api.get(folderCatalogUrl)
      .then((response) => setFolders(response.data?.folders || []))
      .catch(() => {});
    loadDocuments(currentFolder, currentPath);
  };

  const normalizedNavigatorSearch = navigatorSearch.trim().toLowerCase();
  const matchingChildFolders = childFolders.filter((folder) => (
    !normalizedNavigatorSearch || [folderName(folder), folder.folder_path]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(normalizedNavigatorSearch)
  ));
  const matchingDocuments = documents.filter((document) => (
    !normalizedNavigatorSearch || String(document.filename || '').toLowerCase().includes(normalizedNavigatorSearch)
  ));
  const pageCount = Math.max(1, Math.ceil(matchingDocuments.length / FILE_PAGE_SIZE));
  const visibleDocuments = matchingDocuments.slice(filePage * FILE_PAGE_SIZE, (filePage + 1) * FILE_PAGE_SIZE);

  const atMatterRoot = breadcrumbs.length === 0 && currentFolder?.folder_type !== 'location';
  const navigatorTitle = currentPath ? folderName(currentFolder) : matterNumber;
  const navigatorContext = currentPath ? `Matter ${matterNumber}` : 'Matter documents';
  const navigatorSummary = error && !documents.length && !childFolders.length ? '' : loading
    ? 'Loading files…'
    : normalizedNavigatorSearch
      ? `${matchingChildFolders.length + matchingDocuments.length} ${matchingChildFolders.length + matchingDocuments.length === 1 ? 'match' : 'matches'} in this folder`
      : `${childFolders.length} ${childFolders.length === 1 ? 'subfolder' : 'subfolders'} · ${documents.length} ${documents.length === 1 ? 'file' : 'files'} here`;
  const explorerRoot = baseSimulRoot || matterCreateParent;
  const explorerController = useDocumentFileExplorerController({
    selection: {
      documents: visibleDocuments,
      loading,
      locationKey: currentPath,
      onOpenDocument: (document) => navigate(`/documents/${encodeURIComponent(document.document_id)}`),
      shouldUseViewer: shouldUseDocumentFileViewer,
    },
    clipboard: {
      activeFolder: currentFolder || explorerRoot,
      onPaste: performPaste,
      onPasteFiles: (folder, { files }) => setPendingUpload({ folder, files, id: `${Date.now()}-paste` }),
      onRememberError: (error) => setMoveError(error.message || 'Could not copy to the clipboard.'),
      onRemember: ({ action, item, type }) => {
        updateUploadStatus(`${action === 'move' ? 'Cut' : 'Copied'} ${item?.name || item?.filename || type}. Open a folder and paste.`, 4000);
      },
      onPasteComplete: (_clipboard, destination) => {
        updateUploadStatus(`Pasted into ${folderName(destination)}`, 4000);
      },
      onPasteError: (requestError) => {
        setMoveError(requestError?.response?.data?.error || requestError.message || 'Could not paste here.');
      },
    },
  });
  const {
    selectedPreviewDocument,
    selectPreviewDocument,
    openDocument: openDocumentFile,
    fileViewerDocument,
    closeFileViewer,
    fileContextMenu,
    folderContextMenu,
    openFileContextMenu,
    openFolderContextMenu,
    openFileContextMenuFromKey,
    openFolderContextMenuFromKey,
    closeFileContextMenu,
    closeFolderContextMenu,
    canPaste,
    copyDocument,
    cutDocument,
    copyFolder,
    cutFolder,
    pasteIntoFolder,
  } = explorerController;
  const treeChildrenFor =

  (folder) => {
    let candidates;
    const isRoot = folderIdentity(folder) === folderIdentity(explorerRoot);
    if (isRoot) {
      candidates = [
        ...(baseSimulRoot
          ? simulFolders.filter((candidate) => String(candidate.parent_folder_id || '') === String(baseSimulRoot.folder_id))
          : simulRoots),
        ...immediateInternalChildren(''),
      ];
    } else if (folder.folder_type === 'simul') {
      candidates = [
        ...simulFolders.filter((candidate) => String(candidate.parent_folder_id || '') === String(folder.folder_id)),
        ...immediateInternalChildren(relativePathFor(folder)),
      ];
    } else {
      candidates = immediateInternalChildren(normalizePath(folder.folder_path));
    }
    const unique = new Map();
    candidates.forEach((candidate) => {
      const key = `name:${folderName(candidate).toLowerCase()}`;
      if (!unique.has(key)) unique.set(key, candidate);
    });
    return [...unique.values()].sort((left, right) => folderName(left).localeCompare(folderName(right)));
  };
  const columnLineage = (

  () => {
    if (!currentFolder || folderIdentity(currentFolder) === folderIdentity(explorerRoot)) return [];
    if (currentFolder.folder_type === 'simul') return breadcrumbs;
    const parts = currentPath.split('/').filter(Boolean);
    return parts.map((_part, index) => {
      const path = parts.slice(0, index + 1).join('/');
      return internalFolders.find((folder) => normalizePath(folder.folder_path) === path) ||
        (index === parts.length - 1 ? currentFolder : null);
    }).filter(Boolean);
  })();
  const columnParents = [explorerRoot, ...columnLineage].filter((folder, index, items) => (
    folder && items.findIndex((candidate) => folderIdentity(candidate) === folderIdentity(folder)) === index
  ));
  const columnData = columnParents.map((folder, index) => ({
    folder,
    title: index === 0 ? matterNumber : folderName(folder),
    showHeader: index > 0,
    children: index === columnParents.length - 1 ? matchingChildFolders : treeChildrenFor(folder),
    selectedChildKey: columnParents[index + 1] ? folderIdentity(columnParents[index + 1]) : '',
    active: index === columnParents.length - 1,
  }));

  useEffect(() => {
    if (!columnsViewportRef.current) return;
    columnsViewportRef.current.scrollLeft = columnsViewportRef.current.scrollWidth;
  }, [columnParents.length, currentFolder]);

  return (
    <section className="matter-documents" aria-label={`Documents for ${matterNumber}`}>
      <header className="matter-documents__navigator-header">
        {!atMatterRoot ? (
          <button
            type="button"
            className="matter-documents__back"
            aria-label="Up one level"
            title="Up one level"
            onClick={goUp}>
            <AppIcon icon="pi pi-arrow-left" aria-hidden="true" />
          </button>
        ) : (
          <span className="matter-documents__navigator-icon" aria-hidden="true">
            <AppIcon icon="pi pi-folder-open" />
          </span>
        )}
        <span className="matter-documents__navigator-title">
          <strong>{navigatorTitle}</strong>
          <small>{navigatorContext}</small>
        </span>
        <span className="matter-documents__navigator-actions">
          <AppButton
            label="New folder"
            icon="pi pi-folder-plus"
            variant="secondary"
            title={atMatterRoot ? `Create a folder in ${matterNumber}` : `Create a subfolder in ${navigatorTitle}`}
            onClick={() => openNewFolder()} />
          <AppButton label="New document" icon="pi pi-plus" onClick={openNewDocument} />
        </span>
      </header>

      <div className="matter-documents__explorer is-columns">

        <div
          className="matter-documents__main-pane"
          aria-label={`Folder contents for ${navigatorTitle}`}>
          {!atMatterRoot && (
            <nav className="matter-documents__breadcrumbs" aria-label="Matter document folders">
              <button type="button" onClick={goToMatterRoot}>
                {matterNumber}
              </button>
              {breadcrumbs.map((folder) => (
                <React.Fragment key={folderIdentity(folder)}>
                  <AppIcon icon="pi pi-angle-right" aria-hidden="true" />
                  <button type="button" onClick={() => openFolder(folder)}>{folderName(folder)}</button>
                </React.Fragment>
              ))}
              {currentFolder?.folder_type === 'location' && currentPath.split('/').filter(Boolean).map((part, index, parts) => (
                <React.Fragment key={parts.slice(0, index + 1).join('/')}>
                  <AppIcon icon="pi pi-angle-right" aria-hidden="true" />
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </nav>
          )}

          <label className="matter-documents__search">
            <AppIcon icon="pi pi-search" aria-hidden="true" />
            <input
              type="search"
              aria-label={`Search within ${navigatorTitle}`}
              value={navigatorSearch}
              onChange={(event) => {
                setNavigatorSearch(event.target.value);
                setFilePage(0);
              }}
              placeholder="Search this folder" />
          </label>
          <div className="matter-documents__summary" role="status">
            <span>{navigatorSummary}</span>
            {uploadStatus && (
              <span className="matter-documents__upload-status">
                <AppIcon icon="pi pi-upload" aria-hidden="true" />
                {uploadStatus}
              </span>
            )}
          </div>

          {error && <div className="matter-documents-error" role="alert">
            <span>{error}</span>
            <AppButton label="Retry" icon="pi pi-refresh" onClick={() => setRetryAttempt((attempt) => attempt + 1)} />
          </div>}
          {!error ? (
            <MillerColumnExplorer
              className="matter-documents__columns"
              ref={columnsViewportRef}
              role="region"
              aria-label={`Column view for ${matterNumber}`}>
              {columnData.map((column) => (
                <MillerColumn
                  active={column.active}
                  uploadTarget={uploadDropTargetKey === uploadTargetKey('column', column.folder)}
                  className={`matter-documents__column ${internalDropTargetKey === uploadTargetKey('column', column.folder) ? 'is-drop-target' : ''}`}
                  aria-label={`Contents of ${column.title}`}
                  key={folderIdentity(column.folder)}
                  {...externalDropHandlers(column.folder, 'column')}>
                  {column.showHeader && (
                    <MillerColumnHeader>
                      <AppIcon icon={column.active ? 'pi pi-folder-open' : 'pi pi-folder'} aria-hidden="true" />
                      <strong title={column.title}>{column.title}</strong>
                    </MillerColumnHeader>
                  )}
                  <MillerColumnList className="matter-documents__column-list">
                    {column.children.map((folder) => {
                      const selected = column.selectedChildKey === folderIdentity(folder);
                      const fileCount = folderCount(folder, 'descendant_document_count', 'document_count');
                      return (
                        <MillerColumnRow
                          selected={selected}
                          dropTarget={internalDropTargetKey === uploadTargetKey('column-row', folder)}
                          uploadTarget={uploadDropTargetKey === uploadTargetKey('column-row', folder)}
                          className={`matter-documents__column-row ${folder.can_move_internally ? 'has-move-action' : ''}`}
                          key={folderIdentity(folder)}
                          draggable={Boolean(folder.can_move_internally)}
                          onDragStart={(event) => beginInternalDrag(event, 'folder', folder)}
                          onDragEnd={endInternalDrag}
                          onContextMenu={(event) => openFolderContextMenu(event, folder)}
                          {...externalDropHandlers(folder, 'column-row')}>
                          <MillerColumnItem
                            className="matter-documents__column-item"
                            aria-label={`Open folder ${folderName(folder)} in column view`}
                            aria-pressed={selected}
                            onKeyDown={(event) => openFolderContextMenuFromKey(event, folder)}
                            onClick={() => openFolder(folder)}>
                            <AppIcon icon={selected ? 'pi pi-folder-open' : 'pi pi-folder'} aria-hidden="true" />
                            <span>
                              <strong title={folderName(folder)}>{folderName(folder)}</strong>
                              <small>{fileCount} {fileCount === 1 ? 'file' : 'files'}</small>
                            </span>
                            <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
                          </MillerColumnItem>
                          {folder.can_move_internally && (
                            <button
                              type="button"
                              className="matter-documents__column-move"
                              aria-label={`Move folder ${folderName(folder)}`}
                              title="Move folder"
                              onClick={() => openMoveFolder(folder)}>
                              <AppIcon icon="pi pi-arrows-alt" aria-hidden="true" />
                            </button>
                          )}
                        </MillerColumnRow>
                      );
                    })}
                    {column.active && loading && (
                      <MillerColumnEmpty className="matter-documents__column-empty" role="status">
                        <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" />
                        Loading files
                      </MillerColumnEmpty>
                    )}
                    {column.active && !loading && visibleDocuments.map((document) => {
                      const created = formatCreated(document.updated_at || document.first_seen_at);
                      const available = document.available_locally !== false;
                      return (
                        <MillerColumnRow
                          file
                          selected={String(selectedPreviewDocument?.document_id || '') === String(document.document_id)}
                          className="matter-documents__column-row is-file"
                          key={document.document_id}
                          draggable={available}
                          onDragStart={available ? (event) => beginInternalDrag(event, 'file', document) : undefined}
                          onDragEnd={available ? endInternalDrag : undefined}
                          onContextMenu={(event) => openFileContextMenu(event, document)}>
                          <MillerColumnItem
                            className="matter-documents__column-item"
                            aria-label={`Select file ${document.filename || 'Untitled document'}`}
                            aria-pressed={String(selectedPreviewDocument?.document_id || '') === String(document.document_id)}
                            title={available ? 'Double-click to open' : 'This file has not been imported locally yet'}
                            onKeyDown={(event) => {
                              if (openFileContextMenuFromKey(event, document)) {
                                return;
                              } else if (event.key === 'Enter' && available) {
                                event.preventDefault();
                                openDocumentFile(document);
                              }
                            }}
                            onClick={() => selectPreviewDocument(document)}
                            onDoubleClick={available ? () => openDocumentFile(document) : undefined}>
                            <DocumentFileTypeIcon file={document} aria-hidden="true" />
                            <span>
                              <strong title={document.filename || 'Untitled document'}>{document.filename || 'Untitled document'}</strong>
                              <small>
                                {available ? `${document.version_count || 0} version${Number(document.version_count) === 1 ? '' : 's'}` : 'Awaiting import'}
                                {created.date ? ` · ${created.date}` : ''}
                              </small>
                            </span>
                          </MillerColumnItem>
                        </MillerColumnRow>
                      );
                    })}
                    {column.active && !loading && column.children.length === 0 && visibleDocuments.length === 0 && (
                      <MillerColumnEmpty className="matter-documents__column-empty">
                        <AppIcon icon="pi pi-folder-open" aria-hidden="true" />
                        <span>{normalizedNavigatorSearch ? 'No matches' : 'Empty folder'}</span>
                      </MillerColumnEmpty>
                    )}
                  </MillerColumnList>
                  {column.active && pageCount > 1 && (
                    <nav className="matter-documents__pagination" aria-label="File pages">
                      <button
                        type="button"
                        aria-label="Previous file page"
                        disabled={filePage === 0}
                        onClick={() => setFilePage((page) => Math.max(0, page - 1))}>
                        <AppIcon icon="pi pi-chevron-left" aria-hidden="true" />
                      </button>
                      <span>{filePage + 1} / {pageCount}</span>
                      <button
                        type="button"
                        aria-label="Next file page"
                        disabled={filePage >= pageCount - 1}
                        onClick={() => setFilePage((page) => Math.min(pageCount - 1, page + 1))}>
                        <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
                      </button>
                    </nav>
                  )}
                </MillerColumn>
              ))}
              <DocumentExplorerPreviewColumn
                document={selectedPreviewDocument}
                onOpen={selectedPreviewDocument?.available_locally === false ? undefined : openDocumentFile} />
            </MillerColumnExplorer>
          ) : null}
        </div>
      </div>

      {uploadError && <div className="matter-documents-error" role="alert">{uploadError}</div>}
      {pendingUpload && (
        <DocumentTransferManager
          key={pendingUpload.id}
          background
          destinationFolderId={uploadDestinationFolderId(pendingUpload.folder)}
          destinationLabel={uploadDestinationLabel(pendingUpload.folder)}
          matterNumber={pendingUpload.folder?.matter_number || matterNumber}
          folderPath={uploadFolderPath(pendingUpload.folder)}
          initialFiles={pendingUpload.files}
          autoStart
          onNotice={

          (message, kind) => {
            if (!message || kind !== 'alert') return;
            setUploadError(message);
            updateUploadStatus('');
            setPendingUpload(null);
          }}
          onUploadComplete={

          (upload) => {
            const addedCount = (upload?.files || []).filter((file) => file.status === 'complete').length;
            const destination = uploadDestinationLabel(pendingUpload.folder);
            refreshAfterUpload();
            setPendingUpload(null);
            updateUploadStatus(`Added ${addedCount} file${addedCount === 1 ? '' : 's'} to ${destination}`, 4000);
          }} />
      )}

      {newDocumentOpen && (
        <NewMatterDocumentModal
          visible
          matterNumber={matterNumber}
          folderPath={currentPath}
          folderOptions={creationFolderOptions}
          templates={templates}
          submitting={creating}
          error={createError}
          onClose={() => !creating && setNewDocumentOpen(false)}
          onCreated={createDocument} />
      )}
      <FileContextMenu
        menu={fileContextMenu}
        onClose={closeFileContextMenu}
        onMove={openMoveFile}
        onCopyToClipboard={copyDocument}
        onCut={cutDocument}
        onTrash={requestDocumentTrash} />
      <FolderContextMenu
        menu={folderContextMenu}
        onClose={closeFolderContextMenu}
        onNewSubfolder={openNewFolder}
        onMove={openMoveFolder}
        onCopyToClipboard={copyFolder}
        onCut={cutFolder}
        onPaste={pasteIntoFolder}
        canPaste={canPaste} />
      <FolderDestinationModal
        visible={Boolean(moveTarget)}
        itemType={moveTarget?.type || 'file'}
        itemLabel={moveTarget?.item?.filename || moveTarget?.item?.name || folderName(moveTarget?.item)}
        folders={destinationFolders || folders}
        excludedFolderIds={moveExcludedFolderIds}
        excludedDestinationKeys={moveExcludedDestinationKeys}
        requireFolderId={moveTarget?.type === 'folder'}
        preserveSimulLocation={Boolean(
          moveTarget?.item?.source_simul_folder_id ||
          moveTarget?.item?.source_simul_folder_path ||
          moveTarget?.item?.simul_folder_id ||
          moveTarget?.item?.simul_folder_path
        )}
        submitting={moveSubmitting}
        error={moveError}
        defaultCreateParent={matterCreateParent}
        onClose={() => !moveSubmitting && setMoveTarget(null)}
        onCreateFolder={createMoveDestination}
        onSubmit={submitMove} />
      <NewFolderModal
        visible={Boolean(newFolderTarget)}
        form={{
          folder_name: newFolderName,
          is_subfolder: Boolean(newFolderTarget) && newFolderTarget !== matterCreateParent,
          parent_label: newFolderTarget ? folderName(newFolderTarget) : '',
        }}
        submitting={newFolderSubmitting}
        error={newFolderError}
        onChange={(updates) => setNewFolderName(updates.folder_name ?? '')}
        onClose={() => !newFolderSubmitting && setNewFolderTarget(null)}
        onSubmit={submitNewFolder} />
      <DocumentFileViewerModal
        document={fileViewerDocument}
        onClose={closeFileViewer} />
      <DocumentTrashDialog
        request={trashRequest}
        busy={trashSubmitting}
        error={trashError}
        onCancel={closeTrashRequest}
        onConfirm={confirmDocumentTrash} />
    </section>
  );
}

export default MatterDocumentsTab;
